import React, { useRef, useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import useResizeObserver from 'use-resize-observer';

import DynamicIcon from '@/elements/DynamicIcon.js';
import DictionaryText from '@/elements/DictionaryText';

const imageAspectWidth = 16;
const imageAspectHeight = 9;
const duration = 12;
const transitionDuration = .85;

export default function Slider(props) {
    const [activeIndex, setActiveIndex] = useState(null);
    const [paused, setPaused] = useState(false);

    const startTimeRef = useRef(null);
    const elapsedBeforePauseRef = useRef(0);
    const timerRef = useRef(null);

    let getNext = () => { return activeIndex + 1 >= props.data.value.parameterMap.itemList.value.contentList.length ? 0 : activeIndex + 1; }
    let getPrev = () => { return activeIndex - 1 < 0 ? props.data.value.parameterMap.itemList.value.contentList.length - 1 : activeIndex - 1; }

    function resetTimer() {
        clearTimeout(timerRef.current);
        startTimeRef.current = null;
        elapsedBeforePauseRef.current = 0;
    }

    const handleSetIndex = (newIndex) => {
        resetTimer();
        setActiveIndex(newIndex);
    };

    const handleNext = () => handleSetIndex(getNext());
    const handlePrev = () => handleSetIndex(getPrev());

    useEffect(() => {
        if (props.data != null)
            setActiveIndex(0);
    }, [props.data])

    useEffect(() => {
        if (props.data == null || activeIndex === null) return;

        if (paused) {
            if (startTimeRef.current !== null) {
                const now = performance.now();
                const elapsed = (now - startTimeRef.current) / 1000;
                elapsedBeforePauseRef.current += elapsed;
                clearTimeout(timerRef.current);
                startTimeRef.current = null;
            }
            return;
        }

        const remainingTime = duration - elapsedBeforePauseRef.current;
    
        if (remainingTime > 0) {
            startTimeRef.current = performance.now();
            timerRef.current = setTimeout(() => {
                resetTimer();
                setActiveIndex(getNext());
            }, remainingTime * 1000);
        } else {
            setActiveIndex(getNext());
        }
    
        return () => clearTimeout(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, paused]);    

    const sharedProps = {
        activeIndex,
        paused,
        setPaused,
        onChangeNext: handleNext,
        onChangePrev: handlePrev,
        onChangeIndex: handleSetIndex,
        data: (props.data && props.data.value.parameterMap.itemList)
            && props.data.value.parameterMap.itemList.value.contentList,
    };

    return (
        <div className={props.className || ''}>
            {!props.mobileOnly &&
                <DesktopSlider className='hidden sm:flex' {...sharedProps} />
            }

            {!props.desktopOnly &&
                <MobileSlider className='block sm:hidden' {...sharedProps} />
            }
        </div>
    );
}

function DesktopSlider(props) {
    const [dimensions, setDimensions] = useState({});
    const [hover, setHover] = useState(false);
    const [supportsHover, setSupportsHover] = useState(true);

    const container = useRef();

    // For the SlideNavigationButton and the Play/Pause button to work properly, check if the device supports hover or not (like touch devices that don't have cursor).
    // If no, we set the hover state to false to make those buttons to always visible.
    useEffect(() => {
        const mediaQuery = window.matchMedia('(hover: hover)');
        setSupportsHover(mediaQuery.matches);
        setHover(!mediaQuery.matches);
        const handler = (e) => {
            setSupportsHover(e.matches);
            setHover(!e.matches);
        };
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    useResizeObserver({
        ref: container,
        onResize: ({ width, height }) => {
            let unit = imageAspectHeight + imageAspectWidth, containerW = width, textContainerDim = (containerW / unit) * imageAspectHeight;
            setDimensions({ imageWidth: (containerW / unit) * imageAspectWidth, textWidth: textContainerDim, height: textContainerDim });
        }
    });

    return (
        <div className={'flex-row gap-0.5 ' + (props.className || '')} ref={container} style={{ height: dimensions ? (dimensions.height + 'px') : '0px' }}>

            <div className='relative flex overflow-hidden bg-tv-gray-light dark:bg-tv-gray-darkest' style={{ width: dimensions.imageWidth + 'px', height: dimensions.height + 'px' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>

                {props.data ? <div className='relative w-full h-full'>
                    {props.data.map((item, index) => <SlideImage key={index} active={props.activeIndex === index} data={item} paused={props.paused} />)}
                </div> : null}

                {props.data && props.data[props.activeIndex] ? <>
                    <div className='absolute w-full h-full pb-1 flex flex-row justify-between'>
                        <SlideNavigationButton className='w-auto' contentClassName='w-10' visible={hover} iconName='FaChevronLeft' onClick={() => props.onChangePrev()} />
                        <Link className='flex-1' to={props.data[props.activeIndex].parameterMap['url'].value} />
                        {props.paused !== undefined && props.setPaused && hover && (
                            <motion.div
                                className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: hover ? 1 : 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <button
                                    onClick={(e) => { e.stopPropagation(); props.setPaused(!props.paused); }}
                                    className="group pointer-events-auto bg-white dark:bg-tv-yellow bg-opacity-30 hover:bg-opacity-40 dark:bg-opacity-100 text-black dark:text-black rounded-full p-6 shadow-2xl hover:scale-105 transition transition-transform"
                                    aria-label={props.paused ? 'Play Slider' : 'Pause Slider'}
                                >
                                    <DynamicIcon iconName={props.paused ? 'FaPlay' : 'FaPause'} className="text-2xl text-gray-300 group-hover:text-white" />
                                </button>
                            </motion.div>
                        )}
                        <SlideNavigationButton className='w-auto' contentClassName='w-10' right visible={hover} iconName='FaChevronRight' onClick={() => props.onChangeNext()} />
                    </div>
                </> : null}

            </div>

            <div className='relative flex overflow-hidden bg-tv-red-dark dark:bg-tv-gray-darkest' style={{ width: dimensions.textWidth + 'px', height: dimensions.height + 'px' }}>

                {props.data ? <div className='relative w-full h-full'>
                    {props.data.map((item, index) => <DesktopSliderSlideContent dimensions={dimensions} key={index} active={props.activeIndex === index} data={item} />)}
                </div> : null}
                    
                {props.data ? <div className='absolute bottom-0 right-0 h-5 mx-6 mb-6 flex flex-row items-center justify-end gap-1 z-10'>
                    {props.data.map((item, index) => <SliderOrb key={index} index={index} active={props.activeIndex === index} onChangeIndex={props.onChangeIndex} />)}
                </div> : null}

            </div>

        </div >
    );
}

function DesktopSliderSlideContent(props) {
    return (
        <div className={'absolute w-full h-full flex flex-col p-6 text-white ' + (props.active ? 'z-10' : 'z-0')}>

            <div className='flex flex-col h-full text-white overflow-hidden'>

                <div className='flex flex-row gap-6 items-start'>

                    <motion.h1
                        className='flex-1 uppercase font-bold text-xl md:text-2xl dark:text-2xl md:dark:text-3xl tracking-normal md:tracking-wider dark:text-tv-yellow-light'
                        // style={{ transformPerspective: 2500 }}
                        transition={{ duration: transitionDuration }}
                        initial={props.active ? 'in' : 'out'}
                        animate={props.active ? 'in' : 'out'}
                        variants={{ 'in': { opacity: 1, rotateX: 1 }, 'out': { opacity: 0, rotateX: 90 } }}
                    >{props.data.parameterMap['title'].value}</motion.h1>

                    {props.data.parameterMap['icon'].value && <motion.div
                        className='w-8 h-8 md:w-10 md:h-10 my-1 hidden md:block'
                        transition={{ duration: transitionDuration }}
                        initial={props.active ? 'in' : 'out'}
                        animate={props.active ? 'in' : 'out'}
                        variants={{ 'in': { opacity: 1, scale: 1 }, 'out': { opacity: 0, scale: 0.5 } }}>
                        <img className={'w-full h-full'} alt='icon' src={props.data.parameterMap['icon'].value.downloadUrl} />
                    </motion.div>}

                </div>

                <motion.div
                    className='relative flex-1 mt-4 overflow-hidden'
                    transition={{ duration: transitionDuration }}
                    initial={props.active ? 'in' : 'out'}
                    animate={props.active ? 'in' : 'out'}
                    variants={{ 'in': { opacity: 1, rotateX: 0 }, 'out': { opacity: 0, rotateX: -90 } }}>
                    <div className='absolute top-0 left-0 text-xs md:text-sm dark:text-sm md:dark:text-sm font-thin' dangerouslySetInnerHTML={{ __html: props.data.parameterMap['content'].value }} />
                    <div className='absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-tv-red-dark dark:from-tv-gray-darkest' />
                </motion.div>

            </div>

            {/* <motion.div
                className='w-min mt-4'
                transition={{ duration: transitionDuration }}
                initial={props.active ? 'in' : 'out'}
                animate={props.active ? 'in' : 'out'}
                variants={{ 'in': { opacity: 1, rotateX: 0 }, 'out': { opacity: 0, rotateX: -90 } }}> */}
            <Link className={'text-white uppercase font-bold text-xs dark:text-base tracking-wide ' + (props.active ? 'block' : 'hidden')} to={props.data.parameterMap['url'].value}><DictionaryText textKey={'read-more'} /></Link>
            {/* </motion.div> */}

        </div>
    );
}

function MobileSlider(props) {
    const [width, setWidth] = useState(null);
    const [height, setHeight] = useState(null);

    const container = useRef();

    useResizeObserver({
        ref: container,
        onResize: ({ width, height }) => {
            if (container && container.current) {
                setWidth(width);
                setHeight(window.innerHeight - container.current.getBoundingClientRect().y);
            }
        }
    });

    return (
        <div className={'w-full flex flex-col ' + (props.className || '')} ref={container} style={{ height: height ? (height + 'px') : '0px' }}>

            <div className='relative w-full bg-tv-gray-lighter' style={{ height: width ? ((width * (imageAspectHeight / imageAspectWidth)) + 'px') : '0px' }}>
                {props.data ? <div className='relative w-full h-full'>
                    {props.data.map((item, index) => <SlideImage key={index} active={props.activeIndex === index} data={item} paused={props.paused} />)}
                </div> : null}

                {props.data && props.data[props.activeIndex] ? <>
                    <div className='absolute w-full h-full pb-1 flex flex-row top-0 justify-between'>
                        <SlideNavigationButton className='w-auto' contentClassName='w-7' hoverDisabled visible iconName='FaChevronLeft' onClick={() => props.onChangePrev()} />
                        <Link className='flex-1' to={props.data[props.activeIndex].parameterMap['url'].value} />
                        {props.paused !== undefined && props.setPaused &&
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                                <button
                                    onClick={(e) => { e.stopPropagation(); props.setPaused(!props.paused); }}
                                    className="pointer-events-auto bg-white bg-opacity-50 dark:bg-tv-yellow dark:text-black text-black rounded-full p-3 shadow hover:scale-105 transition-transform"
                                    aria-label={props.paused ? 'Play Slider' : 'Pause Slider'}
                                >
                                    <DynamicIcon iconName={props.paused ? 'FaPlay' : 'FaPause'} className="text-xl text-gray-600" />
                                </button>
                            </div>
                        }
                        <SlideNavigationButton className='w-auto' contentClassName='w-7' hoverDisabled right visible iconName='FaChevronRight' onClick={() => props.onChangeNext()} />
                    </div>
                </> : null}

                {props.data ? <div className='absolute bottom-0 w-full h-10 flex flex-row items-center justify-center gap-1'>
                    {props.data.map((item, index) => <SliderOrb key={index} index={index} active={props.activeIndex === index} onChangeIndex={props.onChangeIndex} />)}
                </div> : null}
            </div>

            <div className='w-full flex-1 overflow-hidden p-4'>

                {props.data ? <div className='relative w-full h-full'>
                    {props.data.map((item, index) => <MobileSliderSlideContent key={index} active={props.activeIndex === index} data={item} />)}
                </div> : null}

            </div>

            <div className='bg-tv-gray-light dark:bg-tv-yellow w-full h-8 flex items-center justify-center'>
                <DynamicIcon className='text-md text-tv-gray-dark dark:text-black' iconName={'FaChevronDown'} />
            </div>

        </div>
    );
}

function MobileSliderSlideContent(props) {
    return (
        <motion.div
            className={'flex flex-col h-full ' + (props.active ? '' : 'absolute top-0')}
            transition={{ duration: transitionDuration }}
            initial={'out'}
            animate={props.active ? 'in' : 'out'}
            variants={{ 'in': { opacity: 1, display: 'flex' }, 'out': { opacity: 0, transitionEnd: { display: 'none' } } }}>

            <motion.h1
                className='uppercase text-md dark:text-lg font-bold tracking-tight text-tv-red dark:text-tv-yellow'
                transition={{ duration: transitionDuration }}
                initial={props.active ? 'in' : 'out'}
                animate={props.active ? 'in' : 'out'}
                variants={{ 'in': { opacity: 1, rotateX: 0 }, 'out': { opacity: 0, rotateX: -90 } }}>
                {props.data.parameterMap['title'].value}
            </motion.h1>

            <motion.div
                className='relative flex-1 overflow-hidden mt-2 mb-4'
                transition={{ duration: transitionDuration }}
                initial={props.active ? 'in' : 'out'}
                animate={props.active ? 'in' : 'out'}
                variants={{ 'in': { opacity: 1, rotateX: 0 }, 'out': { opacity: 0, rotateX: -90 } }}>
                <div className='text-xs dark:text-sm dark:text-white' dangerouslySetInnerHTML={{ __html: props.data.parameterMap['content'].value }} />
                <div className='absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-white dark:from-tv-gray-darkest' />
            </motion.div>

            {/* {console.log(props.data.parameterMap['url'])} */}

            <Link className='w-full flex' to={props.data.parameterMap['url'].value}>
                <motion.button
                    className='w-full bg-tv-red dark:bg-tv-yellow flex justify-between items-center'
                    transition={{ duration: transitionDuration }}
                    initial={props.active ? 'in' : 'out'}
                    animate={props.active ? 'in' : 'out'}
                    variants={{ 'in': { opacity: 1, scale: 1 }, 'out': { opacity: 0, scale: 0.5 } }}>

                    <div className='flex flex-col gap-0.5 text-left text-white dark:text-black m-3'>
                        <div className='text-xs dark:text-sm font-semibold dark:font-bold tracking-wider uppercase'><DictionaryText textKey={'read-more'} /></div>
                        <span className='text-xxs dark:text-xs font-thin dark:font-normal normal-case'>{props.data.parameterMap['title'].value + ' ››'}</span>
                    </div>

                    <div className='w-14 h-full bg-tv-red-dark dark:bg-tv-purple flex items-center justify-center'>
                        {props.data.parameterMap['icon'].value && <img className={'w-full h-full p-3'} alt='icon' src={props.data.parameterMap['icon'].value.downloadUrl} />}
                        {!props.data.parameterMap['icon'].value && <DynamicIcon className='text-2xl dark:text-2xl text-tv-yellow-light' iconName={'FaBookOpen'} />}
                    </div>

                </motion.button>
            </Link>

        </motion.div>
    );
}

function SlideImage(props) {
    const imageControls = useAnimation();
    const progressControls = useAnimation();

    const startTimeRef = useRef(null);
    const pauseTimeRef = useRef(null); 
    const elapsedBeforePauseRef = useRef(0);

    useEffect(() => {
        if (props.active) {
            imageControls.start('visible');
        } else {
            imageControls.start('hidden');
        }

        if (props.active) {
            if (props.paused) {
                if (startTimeRef.current !== null) { 
                    pauseTimeRef.current = performance.now();
                    const elapsedSinceStart = (pauseTimeRef.current - startTimeRef.current) / 1000; 
                    elapsedBeforePauseRef.current += elapsedSinceStart;
                    progressControls.stop();
                    startTimeRef.current = null;
                 } else {
                    progressControls.stop();
                 }
            } else {
                startTimeRef.current = performance.now();
                const remainingDuration = duration - elapsedBeforePauseRef.current;

                if (remainingDuration > 0) {
                    progressControls.start('finish', {
                        duration: remainingDuration, 
                        ease: 'linear' 
                    });
                } else {
                    progressControls.set('finish');
                }
                pauseTimeRef.current = null;
            }
        } else {
            progressControls.stop(); 
            progressControls.set('start');
            startTimeRef.current = null;
            pauseTimeRef.current = null;
            elapsedBeforePauseRef.current = 0;
        }
    }, [props.active, props.paused, imageControls, progressControls]);

    return (
        <motion.div
            className='absolute w-full h-full flex overflow-hidden'
            initial={'hidden'}
            animate={imageControls} 
            variants={{
                'visible': { opacity: 1, transition: { duration: transitionDuration } },
                'hidden': { opacity: 0, transition: { duration: transitionDuration } } 
            }}
        >
            {props.data.parameterMap['image'].value &&
                <motion.div
                    className='bg-cover bg-center filter brightness-75 w-full h-full'
                    style={{ backgroundImage: 'url(' + props.data.parameterMap['image'].value.downloadUrl + ')' }}
                    transition={{ ease: 'linear', duration: duration }}
                    initial={'start'}
                    animate={props.active ? 'finish' : 'start'}
                    variants={{
                        'start': { scale: 1.1, rotate: 0.25, transition: { duration: transitionDuration } },
                        'finish': { scale: 1, rotate: 0 }
                    }}
                >
                </motion.div>}

            <div className='absolute w-full h-1 bg-white bottom-0 bg-opacity-50 backdrop-filter backdrop-blur-sm'>
                <motion.div
                    className='h-full bg-white bg-opacity-50'
                    initial="start"       
                    animate={progressControls} 
                    variants={{
                        'start': { width: '0%' }, 
                        'finish': { width: '100%' } 
                    }}
                 />
            </div>
        </motion.div>
    );
}

function SlideNavigationButton(props) {
    const [hover, setHover] = useState(false);

    return (
        <button className={'relative h-full ' + (props.className || '')} onClick={() => props.onClick()} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            <motion.div
                className={'absolute top-0 h-full bg-white dark:bg-tv-yellow bg-opacity-40 dark:bg-opacity-100 z-10 flex items-center justify-center shadow-xl backdrop-filter backdrop-blur-sm ' + (props.right ? 'right-0 ' : 'left-0 ') + (props.contentClassName || '')}
                transition={{ ease: 'linear' }}
                style={{ originX: props.right ? 1 : 0, transformPerspective: 500 }}
                initial={props.visible ? 'visible' : 'hidden'}
                animate={props.visible ? 'visible' : 'hidden'}
                variants={{ 'visible': { opacity: hover && !props.hoverDisabled ? 1 : 0.55, rotateY: 0 }, 'hidden': { opacity: 0, rotateY: props.right ? -90 : 90 } }}>

                <DynamicIcon className='text-md dark:text-lg text-white dark:text-tv-gray-darker' iconName={props.iconName} />

            </motion.div>
        </button>
    );
}

function SliderOrb(props) {
    return (<button onClick={() => props.onChangeIndex(props.index)} className={'w-2 h-2 dark:w-3 dark:h-3 rounded-full bg-white transform transition-all scale-100 hover:scale-125 ' + (props.active ? 'bg-opacity-90' : 'bg-opacity-70')} />);
}